import { PlusIcon, Trash2, AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ProgramListItemDataFragment } from '../../ProgramListing/ProgramListing.api';
import type { ProgramListingItemGroup } from '../../ProgramListing/utils';
import type { AssessmentRunSchema, AssessmentRunsFormData } from '../AssessmentRunsFormConfig';
import { createEmptyAssessmentRun } from '../AssessmentRunsFormConfig';
import { type FormMode } from '../useAssessmentRunsFormState';
import { createDateOnly, durationToDays } from '../utils';
import { ProgramOccurrence, AssessmentRunStatus } from '@/apolloClient.types';
import Button from '@/components/common/Button';
import IconButton from '@/components/common/IconButton';
import Loader from '@/components/common/Loader';
import PageHeader from '@/components/common/PageHeader';
import DatePickerField from '@/components/fields/DatePickerField';
import InputField from '@/components/fields/InputField';
import SelectField from '@/components/fields/SelectField';
import { useFetchGroupsQuery } from '@/pages/GroupManagementPage/GroupListing/GroupList.api';

export interface AssessmentRunsSettingsProps {
    mode: FormMode;
    form: UseFormReturn<AssessmentRunsFormData>;
    program?: ProgramListItemDataFragment | null;
    selectedGroup?: ProgramListingItemGroup | null;
    companyId?: string;
    onNext: () => void;
    onClose: () => void;
}

const AssessmentRunsSettingsStep = ({
    form,
    mode,
    program,
    selectedGroup,
    onNext,
    onClose,
    companyId,
}: AssessmentRunsSettingsProps) => {
    const { t } = useTranslation('programManagementPage');
    const { t: tGlobal } = useTranslation('common');

    const { isSubmitting, isValid } = form.formState;

    const watchedGroupId = useWatch({
        control: form.control,
        name: 'groupId',
    });

    const { data: dataGroups, loading: groupsLoading } = useFetchGroupsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: companyId || '',
            filter: {
                enrolledCourseId: program?.courseInstanceId,
            },
        },
        skip: !companyId || companyId === '' || !program?.courseInstanceId,
    });

    const groupOptions = useMemo(() => {
        if (!dataGroups?.groups.items) {
            return [];
        }

        const baseOptions = dataGroups.groups.items.map(group => ({
            value: group.id,
            label: group.name,
        }));

        if (mode === 'edit') {
            return baseOptions;
        }

        // Filter out groups that already have assessment runs
        return baseOptions.filter(group => !program?.assessmentRuns?.some(run => run.groupId === group.value));
    }, [dataGroups?.groups.items, mode, program?.assessmentRuns]);

    useEffect(() => {
        if (mode !== 'edit') {
            let updatedValues = form.getValues();
            if (groupOptions?.length === 1) {
                const singleGroup = groupOptions[0];
                updatedValues = {
                    ...updatedValues,
                    groupId: singleGroup.value,
                    groupName: singleGroup.label,
                };
            }
            if (program && updatedValues.assessmentRuns.length === 0) {
                updatedValues.assessmentRuns = [
                    createEmptyAssessmentRun(new Date(), durationToDays(program.courseInstance.duration)),
                ];
            }
            form.reset(updatedValues);
        }
    }, [groupOptions, form, program, selectedGroup, mode]);

    const selectedGroupData = useMemo(() => {
        return dataGroups?.groups.items.find(group => group.id === watchedGroupId);
    }, [dataGroups?.groups.items, watchedGroupId]);

    useEffect(() => {
        if (watchedGroupId && selectedGroupData) {
            form.setValue('groupName', selectedGroupData.name);
        }
    }, [watchedGroupId, selectedGroupData, form]);

    // Build a set of locked run IDs (in progress or ended) for the selected group
    const lockedRunIds = useMemo(() => {
        const set = new Set<string>();
        if (!program?.assessmentRuns || !watchedGroupId) {
            return set;
        }

        program.assessmentRuns.forEach(run => {
            if (
                run.groupId === watchedGroupId &&
                (run.status === AssessmentRunStatus.InProgress || run.status === AssessmentRunStatus.Ended)
            ) {
                set.add(run.id);
            }
        });

        return set;
    }, [program?.assessmentRuns, watchedGroupId]);

    const watchedRuns: AssessmentRunSchema[] = useWatch({ control: form.control, name: 'assessmentRuns' });

    const isRunLocked = useCallback(
        (index: number) => {
            const id = watchedRuns?.[index]?.id;
            if (!id) {
                return false;
            }
            return lockedRunIds.has(id);
        },
        [lockedRunIds, watchedRuns]
    );

    useEffect(() => {
        const subscription = form.watch((value, { name, type }) => {
            if (name && name.includes('startDate') && type === 'change') {
                const match = name.match(/assessmentRuns\.(\d+)\.startDate/);
                if (match && program) {
                    const index = parseInt(match[1], 10);
                    const startDate = value.assessmentRuns?.[index]?.startDate;

                    if (startDate) {
                        const days = durationToDays(program.courseInstance.duration);
                        const expectedEndDate = createDateOnly(startDate, days);
                        form.setValue(`assessmentRuns.${index}.endDate`, expectedEndDate);
                    }
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [form, program]);

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'assessmentRuns',
    });

    const handleAddNewRun = useCallback(() => {
        if (program) {
            const days = durationToDays(program.courseInstance.duration);
            const lastRun = fields[fields.length - 1];
            append(createEmptyAssessmentRun(lastRun?.endDate ?? new Date(), days));
        }
    }, [append, fields, program]);

    const handleRemoveRun = useCallback(
        (index: number) => {
            if (fields.length > 1) {
                remove(index);
            }
        },
        [remove, fields.length]
    );

    if (groupsLoading) {
        return <Loader />;
    }

    return (
        <div className="mb-6 flex flex-col gap-5">
            <PageHeader title={t('assessmentRuns.title')} noControlsRowGap />

            <form onSubmit={form.handleSubmit(onNext)} className="flex flex-col gap-5">
                <div className="grid items-end lg:grid-cols-2">
                    <SelectField
                        name="groupId"
                        label={t('assessmentRuns.fields.group.label')}
                        options={groupOptions || []}
                        required
                        placeholder={t('assessmentRuns.fields.group.placeholder')}
                        disabled={mode === 'edit'}
                    />
                </div>
                <div className="space-y-5">
                    {fields.map((field, index) => (
                        <div
                            key={field.id}
                            className="flex flex-col items-start justify-start gap-5 rounded-[10px] bg-neutral-50 p-5"
                        >
                            <div className="inline-flex items-center justify-between self-stretch">
                                <div className="justify-start text-base leading-6 font-bold text-neutral-900">
                                    {t('assessmentRuns.fields.assessmentRun.title', { index: index + 1 })}
                                </div>
                                {fields.length > 1 && !isRunLocked(index) && (
                                    <IconButton
                                        onClick={() => handleRemoveRun(index)}
                                        icon={<Trash2 className="size-4 text-red-500" />}
                                        color="ghost"
                                        className="size-4 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                    />
                                )}
                            </div>

                            <div className="grid w-full gap-4 lg:grid-cols-3">
                                <InputField
                                    name={`assessmentRuns.${index}.name`}
                                    label={t('assessmentRuns.fields.assessmentRun.label')}
                                    placeholder={t('assessmentRuns.fields.assessmentRun.placeholder')}
                                    required
                                    disabled={isRunLocked(index)}
                                />
                                <DatePickerField
                                    name={`assessmentRuns.${index}.startDate`}
                                    label={t('assessmentRuns.fields.startDate.label')}
                                    required
                                    disabled={isRunLocked(index)}
                                />
                                <DatePickerField
                                    name={`assessmentRuns.${index}.endDate`}
                                    label={t('assessmentRuns.fields.endDate.label')}
                                    required
                                    disabled={true}
                                />
                            </div>

                            {isRunLocked(index) && (
                                <div className="mt-2 flex items-start gap-2 rounded bg-yellow-50/70 p-2 text-yellow-900">
                                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-yellow-700" />
                                    <div className="text-sm">{t('assessmentRuns.locked.inProgressWarning')}</div>
                                </div>
                            )}
                        </div>
                    ))}

                    {(fields.length === 0 || program?.occurrence === ProgramOccurrence.Custom) && (
                        <Button
                            outline
                            onClick={handleAddNewRun}
                            compact
                            className="inline-flex items-center gap-1.5 !border-transparent bg-teal-50 !text-teal-600 !outline-teal-200"
                        >
                            <PlusIcon aria-hidden="true" className="size-4 text-teal-600" />
                            {t('assessmentRuns.buttons.addRun')}
                        </Button>
                    )}
                </div>
            </form>
            <div className="inline-flex h-9 items-start justify-end gap-2.5 self-stretch">
                <Button outline onClick={onClose} disabled={isSubmitting} compact>
                    {tGlobal('modal.cancel')}
                </Button>
                <Button color="teal" type="submit" onClick={onNext} disabled={isSubmitting || !isValid} compact>
                    {t('assessmentRuns.buttons.next')}
                </Button>
            </div>
        </div>
    );
};

export default AssessmentRunsSettingsStep;
